import React, { Suspense, lazy, useState } from 'react';
import { X, Loader2, Lock, Check, ShieldCheck, Siren, Ticket } from 'lucide-react';
import { fmt, portal } from './FlightScenes';
import { hasWebGL } from './FlightSequence';

const Showroom = lazy(() => import('./sim/Showroom'));

const Perk = ({ icon: I, label, value }) => (
  <div className="flex items-center gap-2 text-xs text-white/70"><I size={13} className="text-[#C9A84C]" />{label}<b className="ml-auto text-[#4ADE80]">{value}</b></div>
);

function JetCard({ j, on, onPick }) {
  return (
    <button type="button" data-testid={`hangar-jet-${j.id}`} onClick={() => onPick(j.id)}
      className={`w-full text-left rounded-xl border p-3 transition-[border-color,background-color] ${on ? 'border-[#C9A84C] bg-[#C9A84C]/10' : 'border-white/10 bg-white/[0.03] hover:border-white/30'}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold text-white text-sm">{j.name}</span>
        {j.active ? <span className="rounded-full bg-[#4ADE80]/15 px-2 py-0.5 text-[10px] tracking-widest text-[#4ADE80]">ACTIVE</span>
          : j.owned ? <span className="text-[10px] tracking-widest text-white/50">OWNED</span>
            : !j.era_ok ? <span className="flex items-center gap-1 text-[10px] tracking-widest text-[#F87171]"><Lock size={11} /> ERA {j.min_era}+</span>
              : <span className="font-mono text-xs text-[#F3D36B]">{fmt(j.price)}</span>}
      </div>
    </button>
  );
}

function Action({ j, busy, onBuy, onSelect }) {
  const cls = 'h-11 w-full rounded-xl text-sm font-bold tracking-wider flex items-center justify-center gap-2 disabled:opacity-40 transition-opacity';
  if (j.active) return <div data-testid="hangar-active-note" className={`${cls} border border-[#4ADE80]/40 text-[#4ADE80]`}><Check size={16} /> FLYING THIS JET</div>;
  if (j.owned) return <button type="button" data-testid={`hangar-select-${j.id}`} disabled={busy} onClick={() => onSelect(j.id)} className={`${cls} border border-[#C9A84C] text-[#F3D36B] hover:bg-[#C9A84C]/10`}>{busy && <Loader2 size={16} className="animate-spin" />} FLY THIS JET</button>;
  return (
    <button type="button" data-testid={`hangar-buy-${j.id}`} disabled={busy || !j.era_ok || !j.can_afford} onClick={() => onBuy(j.id)}
      className={`${cls} bg-gradient-to-b from-[#F3D36B] to-[#C9A84C] text-black`}>
      {busy && <Loader2 size={16} className="animate-spin" />}{!j.era_ok ? `REQUIRES ERA ${j.min_era}` : !j.can_afford ? `NEED ${fmt(j.price)}` : `BUY · ${fmt(j.price)}`}
    </button>
  );
}

/** Jet showroom: preview each jet on a 3D turntable, buy or switch (server-validated). */
export function Hangar({ jets, busy, error, onBuy, onSelect, onClose }) {
  const [pick, setPick] = useState((jets.find((j) => j.active) || jets[0]).id);
  const j = jets.find((x) => x.id === pick);
  return portal(
    <div className="fixed inset-0 z-[2800] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm p-2 sm:p-4" onClick={onClose}>
      <div data-testid="hangar-modal" onClick={(e) => e.stopPropagation()} className="fl-rise w-full max-w-5xl max-h-[94vh] overflow-y-auto rounded-2xl border border-white/10 bg-[#0b0b0d] grid lg:grid-cols-[1.5fr_1fr]">
        <div className="relative h-[34vh] sm:h-[46vh] lg:h-auto lg:min-h-[520px] bg-[#07080a]">
          {hasWebGL() ? <Suspense fallback={<div className="absolute inset-0 flex items-center justify-center text-white/40"><Loader2 className="animate-spin" /></div>}><Showroom livery={pick} /></Suspense>
            : <div className="absolute inset-0 flex items-center justify-center text-white/40 text-sm">3D preview unavailable</div>}
          <p className="absolute left-4 top-4 text-[11px] tracking-[0.3em] text-[#C9A84C]">TETERBORO PRIVATE HANGAR</p>
          <p data-testid="hangar-preview-name" className="absolute left-4 bottom-4 text-xl sm:text-3xl font-bold text-white">{j.name}</p>
        </div>
        <div className="p-4 sm:p-5 space-y-3">
          <div className="flex items-center justify-between"><h3 className="text-lg font-bold tracking-wide text-white">JET FLEET</h3>
            <button type="button" data-testid="hangar-close" onClick={onClose} className="p-2 text-white/60 hover:text-white transition-colors"><X size={18} /></button></div>
          <div className="space-y-2">{jets.map((x) => <JetCard key={x.id} j={x} on={x.id === pick} onPick={setPick} />)}</div>
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 space-y-2">
            <p className="text-xs italic text-white/55">{j.desc}</p>
            <Perk icon={ShieldCheck} label="Customs risk" value={j.customs_cut ? `-${j.customs_cut} pts` : '—'} />
            <Perk icon={Siren} label="Police intercept chance" value={j.intercept_cut ? `-${j.intercept_cut} pts` : '—'} />
            <Perk icon={Ticket} label="Ticket price" value={j.ticket_discount ? `-${j.ticket_discount}%` : '—'} />
          </div>
          {error && <p data-testid="hangar-error" className="rounded-lg border border-[#F87171]/50 bg-[#7f1d1d]/40 px-3 py-2 text-xs text-[#FECACA]">{error}</p>}
          <Action j={j} busy={busy} onBuy={onBuy} onSelect={onSelect} />
        </div>
      </div>
    </div>
  );
}
